import React, { useState, useEffect } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import Header from "./components/Header";
import ProductDetail from "./components/ProductDetail";
import CartScreen from "./components/CartScreen";
import OrdersScreen from "./components/OrdersScreen";
import AdminLogin from "./components/AdminLogin";
import AdminHome from "./components/AdminHome";
import Support from "./components/Support";
import ComingSoon from "./components/ComingSoon";
import { products as initialProducts } from "./products";
import desktopOrMobile from "./Manager/desktopOrMobile";
import LandingPage from "./components/LandingPage";
import { recordVisit } from "./firebaseConfig";
import { update_cart, fetch_cart } from "./components/cart/cart_storage.js";
import { fetchInfo } from "./info.js";
import Privacy from "./policies/Privacy";
import TermsConditions from "./policies/TermsConditions";
import ReturnPolicy from "./policies/ReturnPolicy";
import MainScreen from "./MainScreen.jsx";

const App = () => {
  const [cart, setCart] = useState([]);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState(initialProducts);
  const [addingToCart, setAddingToCart] = useState(null);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const [comingSoon, setComingSoon] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();

  const inauguration = "2205251223"; // Date
  const targetDate = new Date(
    `20${inauguration.slice(4, 6)}-${inauguration.slice(2, 4)}-${inauguration.slice(0, 2)}T${inauguration.slice(6, 8)}:${inauguration.slice(8, 10)}:00`
  );

  useEffect(() => {
    const checkCountdown = () => {
      const now = new Date();
      const isExpired = now >= targetDate;

      if (isExpired && comingSoon) {
        setComingSoon(false);
        if (location.pathname === "/") navigate("/"); // Only navigate if on root
      } else if (!isExpired && !comingSoon) {
        setComingSoon(true);
      }
    };

    checkCountdown();
    const interval = setInterval(checkCountdown, 1000);
    return () => clearInterval(interval);
  }, [navigate, comingSoon, location.pathname]);

  useEffect(() => {
    const consent = localStorage.getItem("cookieConsent");
    fetchInfo();
    recordVisit();

    const cart_storage = fetch_cart();
    if (cart_storage) setCart(cart_storage);

    if (!consent) {
      setShowCookieBanner(true);
    }
  }, []);

  // Redirect to admin panel when login completes
  useEffect(() => {
    if (isAdminLoggedIn && location.pathname === "/admin/login") {
      navigate("/admin");
    }
  }, [isAdminLoggedIn, location.pathname, navigate]);

  const handleAcceptCookies = () => {

    // Set a test cookie to verify cookie functionality
    document.cookie = "cookieConsent=true; path=/; max-age=31536000"; // 1-year expiry

    // Store consent in localStorage
    localStorage.setItem("cookieConsent", "true");

    // Hide cookie banner
    setShowCookieBanner(false);
  };

  const handleRejectCookies = () => {
    setShowCookieBanner(false);
  };

  const calculateTotal = (items) =>
    items.reduce((sum, item) => sum + item.price * (1 - item.discount), 0);

  const handleAddToCart = (product, size, color, quantity) => {
    setCart([...cart, { ...product, size, color, quantity, id: Date.now() }]);
    setAddingToCart(null);
    if (cart) {
      update_cart(cart);
    }
  };

  const handleCheckout = () => {
    setOrders([...orders, { items: [...cart], id: Date.now() }]);
    setCart([]);
    navigate("/orders");
  };

  const handleSetScreen = (screen) => {
    navigate(screen);
  };

  if (comingSoon) {
    return <ComingSoon targetDate={targetDate} />;
  }

  const updateQuantity = (id, newQuantity) => {
    setCart((prevCart) => {
      const updatedCart = prevCart.map((item) =>
        item.id === id ? { ...item, quantity: Math.max(1, newQuantity) } : item
      );
      update_cart(updatedCart);
      return updatedCart;
    });
  };


  return (
    <div className="min-h-screen bg-primary dark:bg-primary-dark">
      <Header cart={cart} orders={orders} setScreen={handleSetScreen} calculateTotal={calculateTotal} />
      <main className="pt-14 bg-primary dark:bg-primary-dark">
        <Routes>
          <Route path="/privacy-policy" element={<Privacy />} />
          <Route path="/terms-of-service" element={<TermsConditions />} />
          <Route path="/return-policy" element={<ReturnPolicy />} />
          <Route path="/" element={<LandingPage />} />
          <Route
            path="/shop"
            element={
              <MainScreen
                products={products}
                navigate={navigate}
                addingToCart={addingToCart}
                setAddingToCart={setAddingToCart}
                handleAddToCart={handleAddToCart}
              />
            }
          />
          <Route
            path="/product/:id"
            element={
              <ProductDetail
                product={products.find((p) => p.id === Number(window.location.pathname.split("/").pop()))}
                setSelectedProduct={() => navigate("/")}
                handleAddToCart={handleAddToCart}
                setAddingToCart={setAddingToCart}
              />
            }
          />
          <Route
            path="/cart"
            element={
              <CartScreen
                cart={cart}
                setCart={setCart}
                updateCartQuantity={updateQuantity}
                setScreen={handleSetScreen}
                calculateTotal={calculateTotal}
                handleCheckout={handleCheckout}
              />
            }
          />
          <Route
            path="/orders"
            element={<OrdersScreen orders={orders} setScreen={handleSetScreen} calculateTotal={calculateTotal} />}
          />
          <Route path="/support" element={<Support />} />
          <Route
            path="/admin"
            element={<AdminLogin setIsAdminLoggedIn={setIsAdminLoggedIn} />}
          />
          <Route
            path="/admin/dashboard"
            element={
              isAdminLoggedIn ? (
                <AdminHome products={products} setProducts={setProducts} />
              ) : (
                <AdminLogin setIsAdminLoggedIn={setIsAdminLoggedIn} />
              )
            }
          />
        </Routes>
      </main>

      {showCookieBanner && (
        <div className="fixed bottom-0 left-0 w-full bg-primary text-primary-foreground dark:bg-primary-dark dark:text-primary-foreground z-[9999] p-4 flex flex-col items-center gap-3 animate-slide-up transition-transform">
          <p className="text-sm text-center text-black dark:text-white">
            We use cookies to enhance your experience. By continuing, you agree our use of cookies and delivery credentials
          </p>
          <div className="flex gap-4">
            <button
              className="bg-[#DBAE6E] text-black px-6 py-3 font-bold uppercase tracking-wide rounded-md transition duration-200"
              onClick={handleAcceptCookies}
            >
              Accept
            </button>
            <button
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 font-bold uppercase tracking-wide rounded-md transition duration-200"
              onClick={handleRejectCookies}
            >
              X
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default App;
